import React, { useState, useEffect } from 'react';
import { Clock, BookOpen, RefreshCw, ExternalLink, Table, List, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';

export default function DiaryWidget({ recordId, diaryTypeId }) {
  const navigate = useNavigate();
  const [diaryTypes, setDiaryTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline'); // 'timeline' or 'table'

  useEffect(() => { loadDiaryTypes(); }, []);
  useEffect(() => { if (selectedType) loadEntries(); }, [selectedType, recordId]);

  const loadDiaryTypes = async () => {
    try {
      const { data } = await client.get('/diary/types');
      const custom = (data.diary_types || []).filter(dt => !dt.is_system && (dt.tracked_fields || []).length > 0);
      if (diaryTypeId) {
        const single = custom.filter(dt => dt.id === diaryTypeId);
        setDiaryTypes(single);
        if (single.length > 0) setSelectedType(single[0]);
      } else {
        setDiaryTypes(custom);
        if (custom.length > 0) setSelectedType(custom[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      const { data } = await client.get('/diary/entries', {
        params: { diary_type_id: selectedType.id, record_id: recordId, limit: 20 }
      });
      setEntries(data.entries || []);
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });

  if (loading) return null;
  if (diaryTypes.length === 0) return null;

  // Get all unique field names from change_data across entries
  const allFieldNames = [];
  for (const entry of entries) {
    for (const change of (entry.change_data || [])) {
      if (!allFieldNames.includes(change.field_name)) allFieldNames.push(change.field_name);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-base">{selectedType?.icon || '📋'}</span>
          <h3 className="font-semibold text-gray-900 text-sm">{selectedType?.name || 'Diary'}</h3>
          {entries.length > 0 && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{entries.length}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setView(view === 'timeline' ? 'table' : 'timeline')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title={view === 'timeline' ? 'Table view' : 'Timeline view'}>
            {view === 'timeline' ? <Table className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}
          </button>
          <button onClick={loadEntries} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Diary Type Tabs */}
      {diaryTypes.length > 1 && (
        <div className="flex gap-1 px-4 py-2 border-b border-gray-50 overflow-x-auto">
          {diaryTypes.map(dt => (
            <button key={dt.id} onClick={() => setSelectedType(dt)}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-full whitespace-nowrap transition ${
                selectedType?.id === dt.id ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'
              }`}>
              <span>{dt.icon}</span> {dt.name}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="max-h-[400px] overflow-auto">
        {entries.length === 0 ? (
          <div className="text-center py-6">
            <Clock className="w-6 h-6 text-gray-200 mx-auto mb-1" />
            <p className="text-gray-400 text-xs">No changes recorded yet</p>
            <p className="text-gray-300 text-[10px] mt-1">Changes to tracked fields will appear here automatically</p>
          </div>
        ) : view === 'timeline' ? (
          /* Timeline View */
          <div className="divide-y divide-gray-50">
            {entries.map(entry => (
              <div key={entry.id} className="px-4 py-2.5 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-gray-400">{formatDate(entry.entry_date)}</span>
                  <span className="text-[10px] text-gray-400">
                    {entry.creator_first_name} {entry.creator_last_name}
                  </span>
                </div>
                <div className="space-y-1">
                  {(entry.change_data || []).map((change, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-gray-700 min-w-[80px]">{change.field_name}:</span>
                      <span className="text-red-400 line-through">{change.old_value || '(empty)'}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-green-600 font-medium">{change.new_value || '(empty)'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-500 whitespace-nowrap">Date</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-500 whitespace-nowrap">By</th>
                  {allFieldNames.map(name => (
                    <th key={name} className="text-left px-3 py-2 font-medium text-gray-500 whitespace-nowrap">{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(entry.entry_date)}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {entry.creator_first_name} {entry.creator_last_name?.charAt(0)}.
                    </td>
                    {allFieldNames.map(name => {
                      const change = (entry.change_data || []).find(c => c.field_name === name);
                      return (
                        <td key={name} className="px-3 py-2">
                          {change ? (
                            <div>
                              <span className="text-red-400 line-through text-[10px]">{change.old_value || '–'}</span>
                              {' '}
                              <span className="text-green-600 font-medium">{change.new_value || '–'}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300">–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View All link */}
      {entries.length >= 20 && selectedType && (
        <button onClick={() => navigate(`/diary/${selectedType.slug}`)}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 border-t border-gray-100">
          View All <ExternalLink className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
